import React, { useEffect, useState } from 'react';
import * as adminApi from '../api/adminApi';
import { pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import {
  IconLayers,
  IconUtensils,
  IconUsers,
  IconShoppingBag,
  IconStar,
  IconMessageSquare,
  IconLifeBuoy,
  IconInbox,
  IconGift,
  IconShield,
  IconImage,
} from '../components/Icons';

const statDefs = [
  { key: 'categoriesCount', label: 'Categories', Icon: IconLayers, tone: 'clay' },
  { key: 'menuItemsCount', label: 'Menu items', Icon: IconUtensils, tone: 'ember' },
  { key: 'bannersCount', label: 'Banners', Icon: IconImage, tone: 'ocean' },
  { key: 'appUsersCount', label: 'App users', Icon: IconUsers, tone: 'ocean' },
  { key: 'ordersCount', label: 'Orders', Icon: IconShoppingBag, tone: 'slate' },
  { key: 'reviewsCount', label: 'Reviews', Icon: IconStar, tone: 'sage' },
  { key: 'feedbackCount', label: 'Feedback', Icon: IconMessageSquare, tone: 'ocean' },
  { key: 'supportTicketsCount', label: 'Support tickets', Icon: IconLifeBuoy, tone: 'slate' },
  { key: 'supportTicketsOpenCount', label: 'Open tickets', Icon: IconInbox, tone: 'wine' },
  { key: 'rewardsCount', label: 'Rewards', Icon: IconGift, tone: 'ember' },
  { key: 'adminsCount', label: 'Admins', Icon: IconShield, tone: 'clay' },
];

function StatCard({ label, value, Icon, tone }) {
  return (
    <div className="col-6 col-md-4 col-lg-3">
      <Card className="stat-dash-card h-100 shadow-sm">
        <CardBody className="d-flex gap-3 align-items-center py-3">
          <div className={`stat-dash-icon stat-dash-icon--${tone}`} aria-hidden>
            <Icon size={22} />
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="text-secondary small text-uppercase" style={{ letterSpacing: '0.04em', fontSize: '0.68rem' }}>
              {label}
            </div>
            <div className="fs-4 fw-semibold text-dark lh-sm font-sans">{value ?? '—'}</div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await adminApi.fetchDashboardCounts();
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Overview of your store" />
      <ErrorBanner message={error} />
      <div className="row g-3">
        {statDefs.map(({ key, label, Icon, tone }) => (
          <StatCard key={key} label={label} value={data?.[key]} Icon={Icon} tone={tone} />
        ))}
      </div>
    </>
  );
}
